import { Check, Minus } from "lucide-react";
import {
  Container,
  Section,
  Heading,
  Lede,
  Eyebrow,
  Card,
  LinkButton,
  Badge,
} from "../components/ui";
import { Seo } from "../lib/head";
import {
  ABUSE_CEILING_MULTIPLE,
  INCLUDED_IN_EVERY_TIER,
  MODULE_INFO,
  MODULE_KEYS,
  PLAN_CATALOG,
  PORTALS,
  TOKENS_PER_CREDIT,
  TOKENS_PER_SYNTHESIS_RUN,
  approxRunsForTokens,
  creditsPerSynthesisRun,
  creditsToTokens,
  ANNUAL_MONTHS_CHARGED,
  formatAdditionalFacilityPrice,
  formatAnnualTotal,
  formatCredits,
  formatFacilityBand,
  formatFirstFacilityPrice,
  formatModuleTypes,
  formatMonthlyTotal,
  portalsForFamily,
  tiersForFamily,
  type ModuleKey,
  type PlanDefinition,
  type PlanFamily,
} from "@weconsultrx/plan-catalog";

// THIS PAGE DECLARES NO NUMBERS. Every price, band, credit figure, module, and
// portal below is read from @weconsultrx/plan-catalog — the same module the API
// enforces against and the webhook writes from.
//
// It used to declare all of it. `TIERS[]` here said "Professional · 4–10
// facilities · volume pricing" while `FACILITY_BANDS.pro` in the API said
// `{min: 4, max: 10}` at a flat per-unit price with no taper, so the page was
// advertising a discount no customer had ever received. Nothing detected it; a
// human happened to read both files in the same afternoon.
//
// What IS declared here is copy — headings, the explanation of what an AI
// credit is, the FAQ. If you find yourself adding a NUMBER to this file, it
// belongs in the catalogue instead, where a test is watching it.

/**
 * Every tier routes to /demo. There is no self-serve path onto this platform: a
 * tenant needs a signed BAA and facility onboarding before it can hold PHI, so
 * "Start free trial" pointing at the app was offering something we cannot
 * actually deliver at the click of a button.
 *
 * `requiresDemo` is `true` for every entry in the catalogue and is asserted in
 * its tests, so this is not a convention a future card can quietly break.
 */
const DEMO_PATH = "/demo";

const FAMILY_COPY: Record<PlanFamily, { eyebrow: string; heading: string; lede: string }> = {
  ambulatory: {
    eyebrow: "Ambulatory",
    heading: "ASC, dialysis, and custom sites",
    lede:
      "Surgery centers, dialysis clinics, and any center or office that employs a consultant pharmacist without being required to. Rule packs, forms, inspections, and formulary review for the ambulatory workflow.",
  },
  ltc: {
    eyebrow: "Long-term care",
    heading: "SNF and ALF — and everything above",
    lede:
      "Skilled nursing and assisted living, with monthly medication regimen review, resident-level flags, and the prescriber and DON signing loop. Includes every ambulatory workflow at no extra cost, so a mixed portfolio needs one plan, not two.",
  },
};

/** The family shown as the common case. */
const HIGHLIGHTED_FAMILY: PlanFamily = "ltc";

function TierCard({ plan }: { plan: PlanDefinition }) {
  const highlight = plan.family === HIGHLIGHTED_FAMILY && plan.first_facility_cents !== null;
  const priced = plan.first_facility_cents !== null;
  const additional = formatAdditionalFacilityPrice(plan.tier);
  const portals = portalsForFamily(plan.family);
  return (
    <Card
      as="article"
      className={`relative flex h-full flex-col ${
        highlight ? "border-forest-700 ring-1 ring-forest-700" : ""
      }`}
    >
      {highlight ? <Badge className="absolute -top-3 left-6">Most popular</Badge> : null}
      <Heading level={3}>{plan.label}</Heading>
      <p className="mt-2 text-ink-600">{plan.tagline}</p>

      <div className="mt-6">
        <span className="text-4xl font-extrabold tracking-tightest">
          {formatFirstFacilityPrice(plan.tier)}
        </span>
        {/* The unit is spelled out. "$100 / mo" alone would read as the whole
            bill, and this page shipped a flat model for two days — some
            readers will arrive carrying that expectation. */}
        {priced ? (
          <span className="text-sm text-ink-500"> / month, first facility</span>
        ) : null}
        {priced && additional ? (
          <p className="mt-1 text-sm font-semibold text-forest-700">
            then {additional} per additional facility
          </p>
        ) : null}
        <p className="mt-1 text-sm font-medium text-ink-500">
          {formatFacilityBand(plan.tier)}
        </p>
      </div>

      <ul className="mt-6 space-y-2 text-sm text-ink-700">
        <li className="flex items-start gap-2">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest-700" aria-hidden />
          <span>
            {priced ? (
              <>
                <strong className="font-semibold">
                  {formatCredits(plan.ai_credits_per_facility)}
                </strong>{" "}
                per facility, every month
              </>
            ) : (
              <strong className="font-semibold">AI credits sized to your volume</strong>
            )}
          </span>
        </li>
        {MODULE_KEYS.filter((k) => plan.modules[k]).map((k) => (
          <li key={k} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest-700" aria-hidden />
            <span>
              {MODULE_INFO[k].label}{" "}
              <span className="text-ink-500">{formatModuleTypes(k)}</span>
            </span>
          </li>
        ))}
        {/* Portals differ by family — an ASC has no Director of Nursing, and
            nothing in the ambulatory workflow produces a letter for a
            prescriber to sign. Listing all three everywhere was true only
            while every tier had all three. */}
        {portals.map((role) => (
          <li key={role} className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest-700" aria-hidden />
            <span>{PORTALS[role].label}</span>
          </li>
        ))}
        <li className="flex items-start gap-2">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest-700" aria-hidden />
          <span>Every AI feature and every integration</span>
        </li>
        <li className="flex items-start gap-2">
          <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest-700" aria-hidden />
          <span>Signed BAA and HIPAA controls</span>
        </li>
        {!priced ? (
          <li className="flex items-start gap-2">
            <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest-700" aria-hidden />
            <span>Custom rule packs and a named success manager</span>
          </li>
        ) : null}
      </ul>

      <div className="mt-auto pt-8">
        <LinkButton
          to={DEMO_PATH}
          variant={highlight ? "primary" : "secondary"}
          className="w-full justify-center"
        >
          Book a demo
        </LinkButton>
      </div>
    </Card>
  );
}

/**
 * What a real bill looks like at a few facility counts.
 *
 * Graduated pricing is easy to state and hard to picture — "$100 then $75
 * each" does not tell an operator with six homes what they owe. A table of
 * actual totals does, and every figure comes from `monthlyTotalCents`, the same
 * arithmetic Stripe performs from the tier table.
 */
function PriceTable({ family }: { family: PlanFamily }) {
  const plan = tiersForFamily(family)[0]!;
  const counts = [1, 2, 4, 6, 8];
  return (
    <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-surface">
      <table className="w-full text-sm">
        <caption className="sr-only">
          {plan.label} monthly and annual totals by facility count
        </caption>
        <thead>
          <tr className="text-left">
            <th scope="col" className="px-5 py-3 font-semibold text-ink-700">
              Facilities
            </th>
            <th scope="col" className="px-5 py-3 text-right font-semibold text-ink-700">
              Per month
            </th>
            <th scope="col" className="px-5 py-3 text-right font-semibold text-ink-700">
              Per year
            </th>
            <th scope="col" className="px-5 py-3 text-right font-semibold text-ink-700">
              AI credits
            </th>
          </tr>
        </thead>
        <tbody>
          {counts.map((n, i) => (
            <tr key={n} className={i % 2 === 0 ? "bg-canvas/50" : ""}>
              <td className="px-5 py-3 text-ink-800">{n}</td>
              <td className="px-5 py-3 text-right font-medium text-ink-900">
                {formatMonthlyTotal(plan.tier, n)}
              </td>
              <td className="px-5 py-3 text-right text-ink-800">
                {formatAnnualTotal(plan.tier, n)}
              </td>
              <td className="px-5 py-3 text-right text-ink-600">
                {(plan.ai_credits_per_facility * n).toLocaleString("en-US")}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FamilySection({ family, tone }: { family: PlanFamily; tone?: "soft" }) {
  const copy = FAMILY_COPY[family];
  return (
    <Section id={`pricing-${family}`} {...(tone ? { tone } : {})}>
      <Container>
        <div className="max-w-3xl">
          <Eyebrow>{copy.eyebrow}</Eyebrow>
          <Heading level={2} className="mt-3">
            {copy.heading}
          </Heading>
          <Lede className="mt-4">{copy.lede}</Lede>
        </div>
        <div className="mt-10 grid gap-6 lg:grid-cols-2">
          {tiersForFamily(family).map((p) => (
            <TierCard key={p.tier} plan={p} />
          ))}
        </div>
        {/* Graduated pricing is easy to state and hard to picture. An operator
            with six homes should not have to do the arithmetic to find out
            what they owe. */}
        <PriceTable family={family} />
      </Container>
    </Section>
  );
}

/**
 * Whether a family includes a module.
 *
 * Reads the family's FIRST tier, which is safe because the catalogue asserts
 * that modules are identical across the bands within a family — a band is a
 * facility count, never a feature line.
 */
function familyHasModule(family: PlanFamily, key: ModuleKey): boolean {
  return tiersForFamily(family)[0]!.modules[key];
}

export function Pricing() {
  // Sized off the SMALLEST paid tier so the sentence below is true for every
  // paying reader, not just the ones on the plan we would rather sell.
  const smallestPaidCredits = PLAN_CATALOG.ambulatory.ai_credits_per_facility;
  const smallestPaidRuns = approxRunsForTokens(creditsToTokens(smallestPaidCredits));

  return (
    <>
      <Seo
        title="Consultant Pharmacist Software Pricing | WeConsult Rx"
        description="Per-facility pricing for consultant pharmacist software. Ambulatory (ASC, dialysis, custom) from $50/mo, SNF and ALF from $100/mo with ambulatory workflows included, and a lower rate for every facility after the first. Every tier ships a signed BAA and every AI feature; long-term care adds the DON and prescriber portals."
        path="/pricing"
      />

      <Section className="pb-8">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Pricing</Eyebrow>
            <Heading level={1} className="mt-3">
              Pay for the facilities you cover.
            </Heading>
            <Lede className="mt-6">
              Your first facility, then a lower rate for every one after it.
              Pick the family that matches the facilities you serve — long-term
              care includes every ambulatory workflow, so a mixed portfolio
              needs one plan, not two. Nothing is gated behind a higher tier.{" "}
              <a
                href="/roi-calculator"
                className="font-semibold text-forest-700 underline hover:text-forest-900"
              >
                See your ROI
              </a>
              .
            </Lede>
            <p className="mt-4 text-sm text-ink-500">
              Pay annually and get a month free — {ANNUAL_MONTHS_CHARGED} months
              for twelve. Every plan starts with a 30-minute demo; we sign a BAA
              and set up your facilities before any patient data moves.
            </p>
          </div>
        </Container>
      </Section>

      <FamilySection family="ambulatory" />
      <FamilySection family="ltc" tone="soft" />

      {/* ------------------------------------------------------------------
          Included in every tier.

          This section is the brief restated as a page: no AI feature and no
          integration is gated by tier. It renders from INCLUDED_IN_EVERY_TIER
          so that gating one later means deleting a line from the catalogue,
          where a test is watching.
          ------------------------------------------------------------------ */}
      <Section>
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Included everywhere</Eyebrow>
            <Heading level={2} className="mt-3">
              Everything below is in every plan.
            </Heading>
            <Lede className="mt-4">
              There is no AI tier, no integrations add-on, and no compliance
              upsell. A one-site surgery center runs the same software as an
              eight-facility operator — what differs between the two families is
              which clinical workflows and portals are switched on, and nothing
              else.
            </Lede>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {(
              [
                ["AI — every feature, every tier", INCLUDED_IN_EVERY_TIER.ai],
                ["Integrations and workflow", INCLUDED_IN_EVERY_TIER.integrations],
                ["Compliance and security", INCLUDED_IN_EVERY_TIER.compliance],
              ] as const
            ).map(([title, items]) => (
              <Card key={title} as="article" className="h-full">
                <Heading level={3} className="text-lg">
                  {title}
                </Heading>
                <ul className="mt-4 space-y-2 text-sm text-ink-700">
                  {items.map((item) => (
                    <li key={item} className="flex items-start gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-forest-700" aria-hidden />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </Card>
            ))}
          </div>
        </Container>
      </Section>

      {/* ------------------------------------------------------------------
          Portals.

          Its own section rather than three bullets on a card. Portals are the
          part of the platform a buyer never sees during a demo — they are used
          by people who are not in the room — so a pricing page that mentions
          them in passing understates what is being bought.
          ------------------------------------------------------------------ */}
      <Section tone="soft">
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Portals</Eyebrow>
            <Heading level={2} className="mt-3">
              The portals your family includes.
            </Heading>
            <Lede className="mt-4">
              Long-term care includes all three. Ambulatory includes the
              Facility Admin portal — an ASC has no Director of Nursing, and
              nothing in the ambulatory workflow produces a letter for a
              prescriber to sign. No per-seat charge either way, and no cap on
              how many people you invite.
            </Lede>
          </div>
          <div className="mt-10 grid gap-6 md:grid-cols-3">
            {Object.values(PORTALS).map((p) => {
              const families = (["ambulatory", "ltc"] as const).filter((f) =>
                portalsForFamily(f).includes(p.role),
              );
              return (
                <Card key={p.role} as="article" className="h-full">
                  <Heading level={3} className="text-lg">
                    {p.label}
                  </Heading>
                  <p className="mt-3 text-sm text-ink-700">{p.description}</p>
                  {/* Which families actually include it. Without this the
                      section reads as "all three, everywhere" — which it said
                      in prose until 2026-08-29, while the cards above already
                      said otherwise. */}
                  <p className="mt-3 text-xs font-medium text-ink-500">
                    {families.length === 2
                      ? "Included in both families"
                      : "Long-term care only"}
                  </p>
                </Card>
              );
            })}
          </div>
        </Container>
      </Section>

      {/* ------------------------------------------------------------------
          AI credits.

          The one number on this page that needs explaining, so it gets prose
          rather than a table cell. Every figure is computed from the catalogue
          and the measured run cost — including the "about N reviews" line,
          which is derived rather than written down, because a hardcoded
          approximation is the first thing to rot after a model change.
          ------------------------------------------------------------------ */}
      <Section>
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>AI credits</Eyebrow>
            <Heading level={2} className="mt-3">
              What a credit is, and what happens when you run out.
            </Heading>
            <div className="mt-6 space-y-4 text-ink-700">
              <p>
                One credit is {TOKENS_PER_CREDIT.toLocaleString("en-US")} tokens of
                AI work. A full clinical synthesis of one resident — the med
                list, the flags, the interactions, the narrative — averages{" "}
                {TOKENS_PER_SYNTHESIS_RUN.toLocaleString("en-US")} tokens, or
                about {creditsPerSynthesisRun()} credits. The smallest paid plan{"’"}s{" "}
                {smallestPaidCredits.toLocaleString("en-US")} credits is roughly{" "}
                {smallestPaidRuns.toLocaleString("en-US")} full resident reviews a
                month.
              </p>
              <p>
                <strong className="font-semibold text-ink-900">
                  Going over does not stop your clinical work.
                </strong>{" "}
                We warn you at 80% and again when you pass the allowance, and we
                keep running reviews. Declining to summarize a resident carrying
                a critical interaction in order to save a few cents is not a
                trade we are willing to make. The only hard stop on that pool is
                a runaway guard at {ABUSE_CEILING_MULTIPLE}× your allowance,
                which exists to catch a loop, not a busy month.
              </p>
              <p>
                The AI assistant draws on its own separate pool, so a long
                conversation can never eat into the credits your clinical reviews
                need. That pool does pause at its limit — the assistant stops
                answering until the month turns over, while reviews carry on. We
                would rather say so here than have it surprise you.
              </p>
            </div>
          </div>
        </Container>
      </Section>

      {/* ------------------------------------------------------------------
          Family comparison — modules only.

          Three rows, not thirty. The modules are the ONLY thing that differs
          between the families; everything else on this page is identical across
          every tier, and a comparison table padded with rows checked in every
          column is how a pricing page invents a distinction that does not
          exist.
          ------------------------------------------------------------------ */}
      <Section tone="soft">
        <Container>
          <Heading level={2}>Ambulatory vs long-term care</Heading>
          <p className="mt-4 max-w-2xl text-ink-600">
            The only difference between the two families is which clinical
            workflows are switched on. Long-term care is a superset — if you run
            even one SNF or ALF alongside your surgery centers, one long-term
            care plan covers everything.
          </p>
          <div className="mt-8 overflow-x-auto rounded-2xl border border-border bg-surface">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-left">
                  <th className="w-1/2 px-5 py-3 font-semibold text-ink-700">
                    Clinical workflow
                  </th>
                  <th className="px-5 py-3 text-center font-semibold text-ink-700">
                    Ambulatory
                  </th>
                  <th className="bg-forest-50 px-5 py-3 text-center font-semibold text-ink-700">
                    Long-term care
                  </th>
                </tr>
              </thead>
              <tbody>
                {MODULE_KEYS.map((key, i) => (
                  <tr key={key} className={i % 2 === 0 ? "bg-canvas/50" : ""}>
                    <td className="px-5 py-3 text-ink-800">
                      <span className="font-medium">{MODULE_INFO[key].label}</span>{" "}
                      <span className="text-ink-500">{formatModuleTypes(key)}</span>
                      <p className="mt-1 text-[13px] text-ink-500">
                        {MODULE_INFO[key].description}
                      </p>
                    </td>
                    {(["ambulatory", "ltc"] as const).map((family) => (
                      <td
                        key={family}
                        className={`px-5 py-3 text-center align-top ${
                          family === "ltc" ? "bg-forest-50/50" : ""
                        }`}
                      >
                        {familyHasModule(family, key) ? (
                          <Check
                            className="mx-auto h-4 w-4 text-forest-700"
                            aria-label="Included"
                          />
                        ) : (
                          <Minus
                            className="mx-auto h-4 w-4 text-ink-300"
                            aria-label="Not included"
                          />
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Container>
      </Section>

      <Section>
        <Container>
          <div className="max-w-3xl">
            <Eyebrow>Common questions</Eyebrow>
            <Heading level={2} className="mt-3">
              Before you book
            </Heading>
          </div>
          <dl className="mt-8 grid gap-6 md:grid-cols-2">
            {[
              {
                q: "How does the per-facility price work?",
                a: `Your first facility is ${formatFirstFacilityPrice("ltc")} a month on long-term care, ${formatFirstFacilityPrice("ambulatory")} on ambulatory. Every facility after that is ${formatAdditionalFacilityPrice("ltc")} and ${formatAdditionalFacilityPrice("ambulatory")} respectively. Four long-term care homes is ${formatMonthlyTotal("ltc", 4)} a month, not four separate bills.`,
              },
              {
                q: "Is there a discount for paying annually?",
                a: `Yes — one month free. You pay ${ANNUAL_MONTHS_CHARGED} months for twelve. Four long-term care homes is ${formatAnnualTotal("ltc", 4)} a year instead of ${formatMonthlyTotal("ltc", 4)} twelve times.`,
              },
              {
                q: "What happens past eight facilities?",
                a: "We quote it. At that size the conversation is about SLAs, custom rule packs and a named success manager, not a number on a page.",
              },
              {
                q: "We run surgery centers and one nursing home.",
                a: "One long-term care plan covers everything. It includes every ambulatory workflow, so you do not buy both and you do not run two orgs.",
              },
              {
                q: "Which portals do we get?",
                a: "Long-term care includes all three — facility admin, DON and prescriber. Ambulatory includes the facility admin portal: an ASC has no Director of Nursing, and nothing in the ambulatory workflow produces a letter for a prescriber to sign.",
              },
              {
                q: "Why does every tier require a demo?",
                a: "Because a signed BAA and facility onboarding have to come before any patient data moves. We are not willing to put a self-serve button in front of that, and a 30-minute call is faster than the alternative anyway.",
              },
              {
                q: "Is there a contract or a setup fee?",
                a: "Month to month unless you choose annual, and no setup fee. Onboarding, data migration, and rule-pack configuration are part of the plan.",
              },
              {
                q: "Are portal users charged per seat?",
                a: "No. Invite as many people into the portals your plan includes as you need.",
              },
            ].map((item) => (
              <div key={item.q}>
                <dt className="font-semibold text-ink-900">{item.q}</dt>
                <dd className="mt-2 text-sm text-ink-700">{item.a}</dd>
              </div>
            ))}
          </dl>
        </Container>
      </Section>

      <Section tone="forest" className="text-center">
        <Container>
          <Heading level={2}>Ready to talk?</Heading>
          <p className="mx-auto mt-4 max-w-2xl">
            Book a 30-minute call. Bring a sample chart. We{"’"}ll walk you
            through it live and tell you which plan you need — including when
            the answer is the cheapest one.
          </p>
          <div className="mt-8">
            <LinkButton to={DEMO_PATH} size="lg">
              Request a demo
            </LinkButton>
          </div>
        </Container>
      </Section>
    </>
  );
}
